import { router } from 'expo-router'
import { useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { TouchableOpacity, View } from 'react-native'

import { Avatar } from '@/src/elements/avatar'
import { Button } from '@/src/elements/button/default'
import { IMAGE_SIZE } from '@/src/elements/image/image.constants'
import { DateTimePicker, Input } from '@/src/elements/input'
import { LocalityModalInput } from '@/src/elements/input/LocalityModalInput/LocalityModalInput'
import type { TModalInputProps } from '@/src/elements/input/ModalInput/ModalInput'
import { Icon } from '@/src/elements/media/Icon'
import { SelectInput } from '@/src/elements/select-input/SelectInput'
import { Typography } from '@/src/elements/text/Typography'
import type { TMe } from '@/src/features/auth/models/auth.types'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { USER_TITLE } from '@/src/features/user/models/user.enum'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { parseApiError } from '@/src/general/utilities/fetcher/parseApiError'
import { getAvatarUrl } from '@/src/general/utils/getAvatarUrl'
import { getDateWithoutTimezone } from '@/src/general/utils/getDateWithoutTimezone'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate, type TMessageKey } from '@/src/lang/useTranslate'

import type { TEditAccountSettingsPayload } from '../../../api/types/editAccountSettings.types'
import { useSettingsMutation } from '../../../services/useSettingsMutation'
import { fieldValidate, hasValidation } from '../../../utils/fieldValidate'
import { DeleteAccountForm } from '../../components/DeleteAccountForm'
import { PasswordForm } from '../../components/PasswordForm'
import { SettingsProfileButton } from '../../components/SettingsProfileButton/SettingsProfileButton'
import { SettingsProfileInput } from '../../components/SettingsProfileInput/SettingsProfileInput'
import { SettingsScreenWrapper } from '../../components/SettingsScreenWrapper'
import { MailDeliveryIssueAlert } from './MailDeliveryIssueAlert'

const userTitleKeyMap: Record<USER_TITLE, TMessageKey> = {
  [USER_TITLE.MISS]: 'user.title.miss',
  [USER_TITLE.MRS]: 'user.title.mrs',
  [USER_TITLE.MR]: 'user.title.mr',
  [USER_TITLE.FAMILY]: 'user.title.family',
  [USER_TITLE.FATHER]: 'user.title.father',
  [USER_TITLE.SISTER]: 'user.title.sister',
  [USER_TITLE.BROTHER]: 'user.title.brother',
  [USER_TITLE.ORG]: 'user.title.org',
  [USER_TITLE.DEACON]: 'user.title.deacon',
}

export const SettingsAccountScreen = (): ReactNode => {
  const [errors, setErrors] = useState<Partial<Record<keyof TEditAccountSettingsPayload, string>>>({})
  const { t } = useTranslate()
  const { routes } = useRoutes()
  const { data: me } = useMeQuery()
  const [editing, setEditing] = useState<Partial<TEditAccountSettingsPayload>>(meToEditing(me))

  const getInputValue = <Key extends keyof TEditAccountSettingsPayload>(
    key: Key,
  ): NonNullable<TEditAccountSettingsPayload[Key]> | '' =>
    (typeof editing[key] === 'undefined' ? meToEditing(me)[key] : editing[key]) || ''

  const setValue = <Key extends keyof TEditAccountSettingsPayload>(key: Key, value: TEditAccountSettingsPayload[Key]) =>
    setEditing((prev) => ({ ...prev, [key]: value }))

  const setError = (key: keyof TEditAccountSettingsPayload, error: string | undefined) =>
    setErrors((prev) => ({ ...prev, [key]: error }))

  const { mutateAsync } = useSettingsMutation()

  const createSubmitHandler =
    <Key extends keyof TEditAccountSettingsPayload>(key: Key, value?: TEditAccountSettingsPayload[Key]) =>
    async () => {
      const safeValue = value ?? editing[key]
      try {
        if (hasValidation(key)) fieldValidate[key](safeValue)
        await mutateAsync({ [key]: safeValue })
        setValue(key, safeValue)
      } catch (error) {
        setError(key, t(parseApiError(error, key) || 'content.invalid'))
        throw error
      }
    }

  const navigateToAvatars = () => router.navigate(routes.settingsAvatars())

  const emailInputRenderFunction: TModalInputProps['children'] = ({ onSubmitEditing }) => (
    <Input
      inBottomSheet
      value={getInputValue('email')}
      onChangeText={(newEmail: string | undefined) => {
        setError('email', undefined)
        setValue('email', newEmail)
      }}
      placeholder={t('word.email-address')}
      onSubmitEditing={onSubmitEditing}
      errorMessage={errors.email}
    />
  )

  const hasDistinctNotificationEmail = me?.notificationEmail && me?.notificationEmail !== me?.email

  const locationInputValue = getInputValue('location') || undefined

  return (
    <SettingsScreenWrapper testID={TEST_ID.SETTINGS.ACCOUNT.SCREEN}>
      <View className="items-center">
        <TouchableOpacity activeOpacity={0.7} onPress={navigateToAvatars} testID={TEST_ID.AVATAR.ACCOUNT_BUTTON}>
          <Avatar size="3xl" src={getAvatarUrl(me?.avatar, IMAGE_SIZE.AVATAR.BIG)} className="mx-auto" />
          <View className="absolute bottom-0 right-0 rounded-full border-2 border-solid border-white bg-primary-bg-medium p-2">
            <Icon className="color-primary-text-contrast-low" name={ICON.PENCIL} size="sm" />
          </View>
        </TouchableOpacity>
        {!me?.avatar?.isCustom && (
          <Button
            size="sm"
            variant="light"
            colors="default"
            onPress={() => {
              if (me && me.avatar) {
                router.navigate(routes.settingsAvatar(me.avatar.id))
              } else {
                navigateToAvatars()
              }
            }}
          >
            <Typography
              bold
              size="md"
              className="flex flex-row items-center gap-2 font-semibold text-gray-400"
              colors="default"
              contrast="low"
            >
              {me && me.avatar ? (
                <>
                  {me.avatar.name}
                  <Icon name={ICON.INFO_CIRCLE} size="sm" className="text-gray-400" />
                </>
              ) : (
                <FormattedMessage id="user.popins.avatars.choose-my-image" />
              )}
            </Typography>
          </Button>
        )}
      </View>
      <MailDeliveryIssueAlert onUncaughtSubmit={createSubmitHandler('email')}>
        {emailInputRenderFunction}
      </MailDeliveryIssueAlert>
      <SettingsProfileInput
        label={t('word.your-firstname')}
        title={t('word.your-firstname')}
        value={me?.name}
        placeholder={t('user.settings.click-to-fill')}
        onUncaughtSubmit={createSubmitHandler('name')}
      >
        {({ onSubmitEditing }) => (
          <Input
            inBottomSheet
            value={getInputValue('name')}
            onChange={(e) => {
              setError('name', undefined)
              setValue('name', e.nativeEvent.text)
            }}
            placeholder={t('word.your-firstname')}
            onSubmitEditing={onSubmitEditing}
            errorMessage={errors.name}
          />
        )}
      </SettingsProfileInput>
      <LocalityModalInput
        title={t('user.home-location')}
        placeholder={t('user.settings.click-to-fill')}
        onUncaughtSubmit={(data) => {
          setValue('location', data)
          return createSubmitHandler('location', data)()
        }}
        testID={TEST_ID.SETTINGS.LOCATION.MODAL}
        inputTestId={TEST_ID.SETTINGS.LOCATION.INPUT}
        renderTrigger={({ onTrigger }) => (
          <SettingsProfileButton
            onPress={onTrigger}
            testID={TEST_ID.SETTINGS.LOCATION.BUTTON}
            label={t('user.home-location')}
            placeholder={t('user.settings.click-to-fill')}
            value={locationInputValue?.city}
          />
        )}
      />
      <SettingsProfileInput
        label={t('word.your-title')}
        title={t('word.your-title')}
        value={me?.title ? t(userTitleKeyMap[me.title]) : undefined}
        placeholder={t('user.settings.click-to-fill')}
        onUncaughtSubmit={createSubmitHandler('title')}
      >
        <SelectInput<USER_TITLE | ''>
          radius="full"
          items={Object.values(USER_TITLE).map((value) => ({ label: t(userTitleKeyMap[value]), value }))}
          value={getInputValue('title')}
          onValueChange={(value) => {
            setError('title', undefined)
            setValue('title', value || null)
          }}
          placeholderItem={{ label: t('settings.input.title.placeholder'), value: '' }}
          errorMessage={errors.title}
        />
      </SettingsProfileInput>
      <SettingsProfileInput
        label={t(hasDistinctNotificationEmail ? 'word.email-address-connection' : 'words.your-email-address')}
        value={me?.email}
        placeholder={t('user.settings.click-to-fill')}
        onUncaughtSubmit={createSubmitHandler('email')}
      >
        {emailInputRenderFunction}
      </SettingsProfileInput>
      {hasDistinctNotificationEmail && (
        <SettingsProfileInput
          label={t('word.email-address-notification')}
          value={me?.notificationEmail || ''}
          placeholder={t('user.settings.click-to-fill')}
          onUncaughtSubmit={createSubmitHandler('notificationEmail')}
        >
          {({ onSubmitEditing }) => (
            <Input
              value={getInputValue('notificationEmail')}
              onChangeText={(newNotificationEmail) => {
                setError('notificationEmail', undefined)
                setValue('notificationEmail', newNotificationEmail || null)
              }}
              placeholder={t('word.email-address')}
              onSubmitEditing={onSubmitEditing}
              errorMessage={errors.notificationEmail}
            />
          )}
        </SettingsProfileInput>
      )}
      <PasswordForm />
      <SettingsProfileInput
        testID={TEST_ID.SETTINGS.BIRTHDATE.MODAL}
        triggerButtonTestID={TEST_ID.SETTINGS.BIRTHDATE.INPUT}
        confirmButtonProps={{ testID: TEST_ID.SETTINGS.BIRTHDATE.CONFIRM, variant: 'faded' }}
        cancelButtonProps={{ testID: TEST_ID.SETTINGS.BIRTHDATE.CANCEL }}
        label={t('settings.account.birthdate')}
        title={t('settings.account.birthdate')}
        value={me?.birthdate ? getDateWithoutTimezone(me?.birthdate)?.format('D MMMM YYYY') : undefined}
        placeholder={t('user.settings.click-to-fill')}
        onUncaughtSubmit={createSubmitHandler('birthdate')}
      >
        {() => {
          return (
            <View className="flex-row gap-2">
              <DateTimePicker
                testID={TEST_ID.SETTINGS.BIRTHDATE.PICKER}
                value={getDateWithoutTimezone(getInputValue('birthdate'))?.toDate()}
                onChange={(value) => {
                  setError('birthdate', undefined)
                  // Format to UTC then extract date
                  setValue('birthdate', getDateWithoutTimezone(value)?.format('YYYY-MM-DD'))
                }}
              />
              <Button
                size="xs"
                isIconOnly
                startIcon={ICON.CROSS}
                colors="default"
                variant="light"
                contrast="low"
                onPress={() => {
                  setError('birthdate', undefined)
                  setValue('birthdate', null)
                }}
              />
            </View>
          )
        }}
      </SettingsProfileInput>
      <View className="flex w-full flex-row justify-center sm:justify-start">
        <DeleteAccountForm />
      </View>
    </SettingsScreenWrapper>
  )
}

// This function allow to convert the me object to the editing object to get the initialsValue in the form
const meToEditing = (
  me?: TMe,
  // Birthdate from TFullUser is not compatible with editOne which requires (HYPHEN date)
): Partial<TEditAccountSettingsPayload> => ({
  name: me?.name,
  avatarId: me?.avatar?.id,
  location:
    me?.city && me?.countryCode && me?.latitude && me?.longitude
      ? {
          countryCode: me.countryCode,
          city: me.city,
          latitude: me.latitude,
          longitude: me.longitude,
          zipcode: me.zipcode,
          formattedAddress: undefined,
        }
      : undefined,
  email: me?.email,
  notificationEmail: me?.notificationEmail || undefined,
  gender: me?.gender || undefined,
  title: me?.title || undefined,
  lang: me?.lang || undefined,
  birthdate: me?.birthdate ? getDateWithoutTimezone(me?.birthdate)?.format('YYYY-MM-DD') : undefined,
})
